import { ArrowRightLeft, Cloud, DraftingCompass, PiggyBank, Server } from "lucide-react";
import { SolutionGroupContent } from "./types";

export const coreinfra: SolutionGroupContent = {
  key: "coreinfra",
  slug: "coreinfra",
  title: "CoreInfra",
  titleKey: "servicos.items.coreinfra.title",
  icon: Cloud,
  tagline: "A base robusta da sua transformação digital: nuvem bem desenhada, migrada com segurança e operada com eficiência.",
  description: [
    "Toda iniciativa digital depende de uma infraestrutura confiável. O CoreInfra reúne os serviços da Calriz em Google Cloud: desenho da arquitetura, migração de sistemas e data centers, modernização de aplicações e operação do ambiente com controle de custos.",
    "Trabalhamos com as melhores práticas do Google Cloud Architecture Framework, para que o ambiente nasça seguro, escalável e pronto para crescer, seja para um sistema acadêmico, um portal de serviços públicos ou uma plataforma de dados.",
  ],
  concepts: ["Arquitetura", "Migração", "Operação", "Custos"],
  items: [
    {
      key: "gcp",
      slug: "google-cloud-platform",
      title: "Google Cloud Platform",
      icon: Server,
      tagline: "A mesma infraestrutura que roda o Google, disponível para a sua instituição, com quem conhece cada serviço.",
      what: [
        "O Google Cloud Platform (GCP) é a nuvem pública do Google: servidores virtuais (Compute Engine), containers (GKE e Cloud Run), bancos de dados gerenciados (Cloud SQL, Firestore), armazenamento, redes e serviços de dados e IA, cobrados pelo uso e disponíveis em regiões como São Paulo.",
        "Como parceira Google Cloud, a Calriz ajuda a escolher os serviços certos, estruturar a organização, os projetos e o faturamento, e manter o ambiente seguro e econômico, com suporte em português e faturamento em reais.",
      ],
      concepts: ["Compute", "Containers", "Bancos gerenciados", "Rede global"],
      benefits: [
        { title: "Escala sob demanda", text: "Recursos que crescem e diminuem conforme o uso, sem comprar hardware." },
        { title: "Dados no Brasil", text: "Região de São Paulo para baixa latência e aderência à LGPD." },
        { title: "Segurança nativa", text: "Criptografia por padrão, controle de identidade e proteção da rede do Google." },
        { title: "Faturamento local", text: "Contratação e faturamento em reais por meio da Calriz, com apoio comercial." },
      ],
      steps: [
        { title: "Descoberta", text: "Entendemos as cargas de trabalho, os requisitos e o orçamento disponível." },
        { title: "Landing zone", text: "Organização, projetos, rede, identidades e políticas criadas com boas práticas." },
        { title: "Implantação", text: "Provisionamento dos serviços, preferencialmente como código (Terraform)." },
        { title: "Suporte e evolução", text: "Acompanhamento técnico, otimização e novas cargas conforme a demanda." },
      ],
      audience: ["Instituições de ensino", "Órgãos públicos", "Empresas em expansão", "Times de desenvolvimento"],
    },
    {
      key: "migration",
      slug: "migracao-e-modernizacao",
      title: "Migração e Modernização",
      icon: ArrowRightLeft,
      tagline: "Leve sistemas e data centers para a nuvem com planejamento, sem parar a operação, e modernize no caminho.",
      what: [
        "Migrar é mover aplicações, bancos de dados e arquivos do data center local para a nuvem. Pode ser uma mudança direta (lift and shift), com ajustes leves de plataforma ou uma reconstrução para aproveitar serviços gerenciados. Cada sistema tem a estratégia que faz mais sentido.",
        "Modernizar é dar o passo seguinte: quebrar sistemas monolíticos, adotar containers e serverless, automatizar implantações e usar bancos gerenciados, reduzindo custo de manutenção e acelerando a entrega de novas funcionalidades.",
      ],
      concepts: ["Inventário", "Estratégia", "Migração em ondas", "Containers"],
      benefits: [
        { title: "Sem surpresas", text: "Inventário completo e plano por sistema antes de qualquer mudança." },
        { title: "Operação preservada", text: "Janelas planejadas, testes e plano de retorno para cada onda." },
        { title: "Fim do hardware legado", text: "Menos gastos com servidores, energia, licenças e contratos de manutenção." },
        { title: "Entrega mais rápida", text: "Aplicações modernizadas com deploy automatizado e atualizações frequentes." },
      ],
      steps: [
        { title: "Avaliação", text: "Levantamento de servidores, dependências e custos atuais do ambiente." },
        { title: "Plano de migração", text: "Estratégia para cada sistema, ondas, cronograma e estimativa de custos na nuvem." },
        { title: "Migração", text: "Execução em ondas com testes, validação dos usuários e virada controlada." },
        { title: "Modernização", text: "Evolução para containers, serverless e serviços gerenciados onde traz retorno." },
      ],
      audience: ["Instituições com data center próprio", "Órgãos com sistemas legados", "Empresas com contratos de hosting vencendo", "Times de TI sobrecarregados"],
    },
    {
      key: "architecture",
      slug: "arquitetura-e-design",
      title: "Arquitetura e Design",
      icon: DraftingCompass,
      tagline: "Ambientes desenhados para serem seguros, resilientes e econômicos desde o primeiro dia.",
      what: [
        "Arquitetura de nuvem é o desenho de como os serviços se conectam: rede, identidades, bancos de dados, aplicações, backups e monitoramento. Decisões tomadas aqui definem o custo, a segurança e a capacidade de crescer do ambiente por anos.",
        "Nossos arquitetos certificados seguem o Google Cloud Architecture Framework e entregam diagramas, documentação e infraestrutura como código, para que o ambiente seja reproduzível, auditável e fácil de evoluir pela sua equipe.",
      ],
      concepts: ["Rede", "Identidade", "Alta disponibilidade", "Infra como código"],
      benefits: [
        { title: "Segurança por padrão", text: "Rede segmentada, menor privilégio e criptografia previstos no desenho." },
        { title: "Resiliência", text: "Redundância entre zonas e estratégia de recuperação definida." },
        { title: "Custo previsível", text: "Dimensionamento correto e escolha dos serviços com melhor custo-benefício." },
        { title: "Documentação clara", text: "Diagramas e decisões registradas para a equipe manter e evoluir." },
      ],
      steps: [
        { title: "Requisitos", text: "Levantamento de requisitos de negócio, desempenho, segurança e orçamento." },
        { title: "Desenho", text: "Arquitetura de referência com diagramas e alternativas comparadas." },
        { title: "Validação", text: "Revisão com a equipe do cliente e prova de conceito quando necessário." },
        { title: "Entrega como código", text: "Implantação com Terraform e documentação para a operação." },
      ],
      audience: ["Novos projetos em nuvem", "Ambientes que cresceram sem planejamento", "Equipes de arquitetura e desenvolvimento", "Projetos sujeitos a auditoria"],
    },
    {
      key: "finops",
      slug: "operacao-e-finops",
      title: "Operação e FinOps",
      icon: PiggyBank,
      tagline: "Ambiente operado com disciplina e cada real gasto na nuvem sob controle e com retorno.",
      what: [
        "Operar a nuvem é manter o ambiente saudável no dia a dia: atualizações, monitoramento, ajustes de capacidade, gestão de acessos e resposta a incidentes. Sem esse cuidado, sistemas ficam vulneráveis e custos crescem sem que ninguém perceba.",
        "FinOps é a prática de gestão financeira da nuvem: dar visibilidade de quem gasta o quê, definir orçamentos e alertas, usar descontos por compromisso de uso e eliminar desperdícios, unindo TI e área financeira nas decisões.",
      ],
      concepts: ["Monitoramento", "Orçamentos", "Rightsizing", "Descontos"],
      benefits: [
        { title: "Custos visíveis", text: "Painéis de gasto por projeto, área e serviço, atualizados diariamente." },
        { title: "Menos desperdício", text: "Recursos ociosos e superdimensionados identificados e ajustados." },
        { title: "Orçamento respeitado", text: "Alertas e limites que evitam surpresas na fatura." },
        { title: "Ambiente saudável", text: "Atualizações, monitoramento e ajustes feitos de forma contínua." },
      ],
      steps: [
        { title: "Visibilidade", text: "Organização de rótulos, exportação do faturamento e painéis de custo." },
        { title: "Otimização", text: "Rightsizing, desligamento de ociosos e descontos por compromisso de uso." },
        { title: "Governança", text: "Orçamentos, alertas e políticas para novos recursos." },
        { title: "Rotina de operação", text: "Revisões mensais de custo e saúde do ambiente com recomendações." },
      ],
      audience: ["Instituições com fatura de nuvem crescente", "Áreas financeiras e de compras", "Equipes de TI enxutas", "Órgãos com orçamento anual fixo"],
    },
  ],
};
